import Http from "./Http";
import queryString from "query-string";

export class LivestreamsService extends Http {
  /**
   * Accept/Reject co stream invitation
   */
  acceptRejectInvite({ id, actionType }) {
    return this.put(`/streams/invites/${id}`, { actionType });
  }

  /**
   * Ban participant
   */
  banParticipant({ id }) {
    return this.post(`/streams/participants/${id}/ban`);
  }

  /**
   * Cancel scheduled stream
   */
  cancelScheduledStream(uniqueKey) {
    return this.delete(`/streams/scheduled/${uniqueKey}`);
  }

  /**
   * Change participant role
   */
  changeParticipantRole({ id, body }) {
    return this.put(`/streams/participants/${id}/role`, body);
  }

  /**
   * Create stream
   */
  createStream(body) {
    return this.post("/streams", body);
  }

  /**
   * Delete all participant messages
   */
  deleteAllParticipantMessages({ id }) {
    return this.delete(`/streams/participants/${id}/messages`);
  }

  /**
   * Delete message
   */
  deleteMessage({ id }) {
    return this.delete(`/streams/messages/${id}`);
  }

  /**
   * Delete stream goal
   */
  deleteStreamGoal() {
    return this.delete("/streams/goal");
  }

  /**
   * End game by type (bottle / wheel)
   */
  endGame({ uniqueKey, type }) {
    return this.delete(`/streams/${uniqueKey}/games`, {
      data: {
        type,
      },
    });
  }

  /**
   * End livestream
   */
  endLivestream() {
    return this.delete(`/streams`);
  }

  /**
   * End stream
   */
  endStream(body) {
    return this.delete("/streams", body);
  }

  /**
   * Get creator streams
   */
  getCreatorStreams(uniqueKey, params) {
    return this.get(`/streams/users/${uniqueKey}`, { params });
  }

  /**
   * Get invite list of creators
   */
  getInviteList({ uniqueKey, params }) {
    return this.get(`/streams/${uniqueKey}/creators`, {
      params,
      paramsSerializer: (params) =>
        queryString.stringify(params, {
          skipEmptyString: true,
          skipNulls: true,
        }),
    });
  }

  /**
   * Get livestream details
   */
  getLivestreamDetails({ uniqueKey }) {
    return this.get(`/streams/${uniqueKey}`);
  }

  /**
   * Get livestream messages
   */
  getLivestreamMessages(uniqueKey, params) {
    return this.get(`/streams/${uniqueKey}/recorded/messages`, { params });
  }

  /**
   * Get participants
   */
  getParticipants({ uniqueKey, params }) {
    return this.get(`/streams/${uniqueKey}/participants`, { params });
  }

  /**
   * Get past streams
   */
  getPastStreams(params) {
    return this.get("/streams/recordings", { params });
  }

  /**
   * Get recording status of active stream
   */
  getRecordingStatus() {
    return this.get(`/streams/recordings/status`);
  }

  /**
   * Get scheduled streams
   */
  getScheduledStreams(params) {
    return this.get("/streams/scheduled", { params });
  }

  /**
   * Get stream messages
   */
  getStreamMessages({ uniqueKey, body }) {
    return this.get(`/streams/${uniqueKey}/messages`, body);
  }

  /**
   * Get streams
   */
  getStreams(params) {
    return this.get("/streams", { params });
  }

  /**
   * Get suggested livestreams
   */
  getSuggestedLivestreams(params) {
    return this.get("/users/suggestions", { params });
  }

  /**
   * Invite participant
   */
  inviteParticipant({ id }) {
    return this.post(`/streams/participants/${id}/invites`);
  }

  /**
   * Join to the stream
   */
  joinStream({ uniqueKey }) {
    return this.post(`/streams/${uniqueKey}/participants`);
  }

  /**
   * Leave the stream
   */
  leaveStream({ uniqueKey }) {
    return this.delete(`/streams/${uniqueKey}/participants`);
  }

  /**
   * Make a co-streamer a viewer
   */
  makeCoStreamerAViewer({ id }) {
    return this.delete(`/streams/co-streamers/${id}`);
  }

  /**
   * Mute participant
   */
  muteParticipant({ id }) {
    return this.post(`/streams/participants/${id}/mute`);
  }

  /**
   * Reject all invitations
   */
  rejectAll() {
    return this.delete(`/streams/invites`);
  }

  /**
   * Report stream
   */
  reportStream({ id, report }) {
    return this.post(`/streams/${id}/reports`, report);
  }

  /**
   * Request invite to co streaming
   */
  requestInviteToCoStreaming({ uniqueKey }) {
    return this.post(`/streams/${uniqueKey}/invites`);
  }

  /**
   * Send invite to co streaming
   */
  sendInviteToCoStreaming({ uniqueKey }) {
    return this.post(`/streams/${uniqueKey}/invites`);
  }

  /**
   * Send livestream tip
   */
  sendLivestreamTip({ uniqueKey, body }) {
    return this.post(`/streams/${uniqueKey}/tips`, body);
  }

  /**
   * Send message to stream
   */
  sendMessageToStream({ uniqueKey, body }) {
    return this.post(`/streams/${uniqueKey}/messages`, body);
  }

  /**
   * Set stream reminder
   */
  setStreamReminder(uniqueKey) {
    return this.post(`/streams/scheduled/${uniqueKey}/reminders`);
  }

  /**
   * Spin the bottle
   */
  spinTheBottle({ uniqueKey, body }) {
    return this.post(`/streams/${uniqueKey}/games/spin`, body);
  }

  /**
   * Start game by type (bottle / wheel)
   */
  startGame({ uniqueKey, type }) {
    return this.post(`/streams/${uniqueKey}/games`, {
      type,
    });
  }

  /**
   * Start stream recording
   */
  startRecording() {
    return this.post(`/streams/recording`);
  }

  /**
   * Update livestream preview image
   */
  updateLivestreamPreviewImage({ previewImageId }) {
    return this.put(`/streams/preview-images`, { previewImageId });
  }

  /**
   * Update scheduled stream
   */
  updateScheduledStream(uniqueKey, body) {
    return this.put(`/streams/scheduled/${uniqueKey}`, body);
  }

  /**
   * Update stream
   */
  updateStream(body) {
    return this.put("/streams", body);
  }

  /**
   * Update stream goal
   */
  updateStreamGoal(body) {
    return this.put("/streams/goal", body);
  }
}

LivestreamsService.$displayName = "LivestreamsService";

export default new LivestreamsService();
